import React, { useCallback, useEffect, useState } from "react";
import api, { fmtDate, fmtTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Search, MessageSquareText, Check, RefreshCw, Trash2, Pencil, Save, CheckCheck, Share2 } from "lucide-react";
import { compartilharTexto } from "@/lib/whatsapp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NovaMensagem } from "@/components/NovaMensagem";
import { toast } from "sonner";

const ST = {
  pendente: ["Pendente", "bg-amber-100 text-amber-800"],
  gerada: ["Gerada", "bg-slate-100 text-slate-700"],
  copiada: ["Copiada", "bg-sky-100 text-sky-800"],
  aberta_whatsapp: ["Compartilhada", "bg-teal-100 text-teal-800"],
  compartilhada: ["Compartilhada", "bg-teal-100 text-teal-800"],
  enviada: ["Enviada", "bg-emerald-100 text-emerald-800"],
  falhou: ["Falhou", "bg-red-100 text-red-800"],
};

export default function Mensagens() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pendente");
  const [evento, setEvento] = useState("todas");
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/mensagens/categorias").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/mensagens?status=${status === "todas" ? "" : status}&evento=${evento === "todas" ? "" : evento}&q=${encodeURIComponent(q)}`); setItems(data); } catch { /* mantém lista atual */ }
    setLoading(false);
  }, [q, status, evento]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const marcar = async (m) => { await api.post(`/mensagens/${m.id}/enviada`); toast.success("Marcada como enviada"); load(); };
  const excluir = async (m) => { await api.delete(`/mensagens/${m.id}`); load(); };
  const [waBusy, setWaBusy] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState([]);
  const [lote, setLote] = useState(false);
  const canBulk = ["administrador", "financeiro"].includes(user?.role);
  const pendentes = items.filter((m) => m.status !== "enviada");
  const compartilhar = async (m) => {
    if (waBusy) return;
    setWaBusy(m.id);
    await compartilharTexto({ texto: m.texto, titulo: m.titulo, mensagem_id: m.status === "enviada" ? null : m.id });
    await load(); setWaBusy(null);
  };
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSel(sel.length === pendentes.length ? [] : pendentes.map((m) => m.id));
  useEffect(() => { setSel([]); }, [status, q]);
  const enviarLote = async () => {
    setLote(true);
    try { for (const id of sel) await api.post(`/mensagens/${id}/enviada`); toast.success(`${sel.length} marcada(s) como enviada(s)`); setSel([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao marcar"); load(); }
    finally { setLote(false); }
  };
  const salvarEdicao = async () => {
    setSaving(true);
    try {
      await api.put(`/mensagens/${edit.id}`, { texto: edit.texto, telefone: edit.telefone });
      toast.success("Mensagem atualizada");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="mensagens-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-extrabold tracking-tight">Mensagens</h1><p className="text-slate-500 text-sm">Caixa de mensagens — copie ou compartilhe o texto pelo aplicativo de sua preferência e confirme o envio.</p></div>
        <div className="flex gap-2">
          <NovaMensagem cats={cats} onCreated={load} />
          <Button variant="outline" onClick={load} className="gap-2" data-testid="msg-refresh"><RefreshCw className="w-4 h-4" /> Atualizar</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pendente" data-testid="msg-tab-pendente">Pendentes</TabsTrigger>
            <TabsTrigger value="enviada" data-testid="msg-tab-enviada">Enviadas</TabsTrigger>
            <TabsTrigger value="falhou" data-testid="msg-tab-falhou">Falharam</TabsTrigger>
            <TabsTrigger value="todas" data-testid="msg-tab-todas">Todas</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." className="pl-9" data-testid="msg-search" />
        </div>
        <Select value={evento} onValueChange={setEvento}>
          <SelectTrigger className="w-56" data-testid="msg-categoria"><SelectValue placeholder="Todas as categorias" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas as categorias</SelectItem>
            {cats.map((c) => <SelectItem key={c.evento} value={c.evento}>{c.titulo}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {canBulk && pendentes.length > 0 && (
        <div className="trt-card p-3 flex items-center gap-3 flex-wrap" data-testid="msg-bulk-bar">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={sel.length > 0 && sel.length === pendentes.length} onCheckedChange={toggleAll} data-testid="msg-select-all" />
            Selecionar todas pendentes ({pendentes.length})
          </label>
          <span className="text-xs text-slate-500">{sel.length} selecionada(s)</span>
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button size="sm" variant="outline" onClick={enviarLote} disabled={!sel.length || lote} className="gap-1" data-testid="msg-bulk-mark">
              <CheckCheck className="w-4 h-4" /> Confirmar envio das selecionadas
            </Button>
          </div>
        </div>
      )}

      {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div>
        : items.length === 0 ? <div className="trt-card py-10 text-center text-slate-400">Nenhuma mensagem.</div>
        : <div className="space-y-3">
          {items.map((m) => {
            const [lbl, cls] = ST[m.status] || ST.pendente;
            return (
              <div key={m.id} className={`trt-card p-4 ${sel.includes(m.id) ? "ring-2 ring-gold" : ""}`} data-testid={`msg-row-${m.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex items-start gap-3">
                    {canBulk && m.status !== "enviada" && <Checkbox checked={sel.includes(m.id)} onCheckedChange={() => toggle(m.id)} className="mt-1" data-testid={`msg-select-${m.id}`} />}
                    <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{m.cliente_nome}</span>
                      <span className="text-xs text-slate-500">{m.telefone || "sem telefone"}</span>
                      <Badge className={`${cls} border-0`}>{lbl}</Badge>
                      {m.categoria && <Badge variant="outline" className="text-[10px]">{m.categoria}</Badge>}
                      <span className="text-xs text-slate-400">{m.titulo}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{fmtDate(m.created_at)} {fmtTime(m.created_at)}{m.enviada_em && ` • enviada ${fmtDate(m.enviada_em)} (${m.canal})`}</div>
                    {m.erro && <div className="text-xs text-red-600 mt-1">{m.erro}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {m.status !== "enviada" && <Button size="sm" variant="outline" onClick={() => setEdit({ id: m.id, texto: m.texto, telefone: m.telefone || "" })} className="gap-1" data-testid={`msg-edit-${m.id}`}><Pencil className="w-4 h-4" /> Editar</Button>}
                    <Button size="sm" onClick={() => compartilhar(m)} disabled={waBusy === m.id} className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`msg-share-${m.id}`}>{waBusy === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Copiar / Compartilhar</Button>
                    {m.status !== "enviada" && <Button size="sm" variant="outline" onClick={() => marcar(m)} className="gap-1" title="Confirmar que a mensagem foi enviada" data-testid={`msg-done-${m.id}`}><Check className="w-4 h-4" /> Confirmo que enviei</Button>}
                    <Button size="sm" variant="ghost" onClick={() => excluir(m)} className="text-red-600 hover:bg-red-50" data-testid={`msg-del-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-sm mt-3 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 break-words" data-testid={`msg-text-${m.id}`}>{m.texto}</p>
                {m.editada_em && <div className="text-[11px] text-slate-400 mt-1">editada por {m.editada_por}</div>}
              </div>
            );
          })}
        </div>}

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg" data-testid="msg-edit-dialog">
          <DialogHeader><DialogTitle>Editar mensagem</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-xs">Telefone</Label><Input value={edit.telefone} onChange={(e) => setEdit({ ...edit, telefone: e.target.value })} className="mt-1" data-testid="msg-edit-phone" /></div>
              <div><Label className="text-xs">Texto da mensagem</Label><Textarea rows={7} value={edit.texto} onChange={(e) => setEdit({ ...edit, texto: e.target.value })} className="mt-1 text-sm" data-testid="msg-edit-text" /></div>
              <div className="text-xs text-slate-500">{edit.texto.length} caracteres</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button onClick={salvarEdicao} disabled={saving || !edit?.texto?.trim()} className="bg-onyx text-white gap-2" data-testid="msg-edit-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
